import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { BUDGET_STATUS } from 'shared/utils/budgetIntelligence';
import {
  Bell,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Flame,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

export default function BudgetAlertsBanner({ budgetData }) {
  const { theme, isDark } = useTheme();
  const { formatCurrency } = useCurrency();
  const [modalOpen, setModalOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (!budgetData || !budgetData.activeAlerts) return null;

  const { activeAlerts, currentDay, daysRemaining, monthProgressPct } = budgetData;

  if (activeAlerts.length === 0 || isDismissed) {
    return null;
  }

  const criticalCount = activeAlerts.filter(
    (a) => a.status === BUDGET_STATUS.OVERBUDGET
  ).length;
  const warningCount = activeAlerts.filter(
    (a) => a.status === BUDGET_STATUS.WARNING
  ).length;

  const accentColor = criticalCount > 0 ? '#EF4444' : '#F59E0B';
  const bannerBg = isDark ? '#1F2937' : '#FFFFFF';

  // Helper to format advice safely in user's currency
  const getFormattedAdvice = (item) => {
    if (item.status === BUDGET_STATUS.OVERBUDGET) {
      return `Exceeded limit by ${formatCurrency(item.overspentAmount)}. Pause non-essential spending.`;
    }
    if (item.percentage >= 80) {
      return `Only ${formatCurrency(item.remaining)} left. Cap spending at ${formatCurrency(item.targetDailySpend)}/day to stay within limit.`;
    }
    if (item.projectedOverspend > 0) {
      return `At current pace (${formatCurrency(item.dailyBurnRate)}/day), projected to exceed limit by ${formatCurrency(item.projectedOverspend)}. Reduce daily spend to ${formatCurrency(item.targetDailySpend)}/day.`;
    }
    return `${item.percentage.toFixed(0)}% used with ${daysRemaining} days left. Daily budget: ${formatCurrency(item.targetDailySpend)}.`;
  };

  return (
    <>
      {/* Sleek, Non-Clumsy Compact Notification Strip */}
      <div
        className="mb-6 px-4 py-3 rounded-xl border flex items-center justify-between gap-3 shadow-sm transition-all duration-200"
        style={{
          backgroundColor: bannerBg,
          borderColor: `${accentColor}40`,
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{
              backgroundColor: `${accentColor}18`,
              color: accentColor,
            }}
          >
            <Bell className="w-4 h-4 animate-bounce" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-xs sm:text-sm" style={{ color: theme.text }}>
                {criticalCount > 0
                  ? `${criticalCount} Over Budget & ${warningCount} Warning${warningCount === 1 ? '' : 's'}`
                  : `${activeAlerts.length} Budget Overspending Warning${activeAlerts.length === 1 ? '' : 's'}`}
              </span>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${accentColor}20`,
                  color: accentColor,
                }}
              >
                Day {currentDay} Pace Alert
              </span>
            </div>
            <p className="text-[11px] truncate hidden sm:block" style={{ color: theme.textSecondary }}>
              Pace anomalies detected in {activeAlerts.slice(0, 3).map((a) => a.category).join(', ')}
              {activeAlerts.length > 3 ? ` +${activeAlerts.length - 3} more` : ''}.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            onClick={() => setModalOpen(true)}
            className="text-xs font-semibold px-3 py-1.5 h-8 flex items-center gap-1.5 rounded-lg shadow-none"
            style={{
              backgroundColor: `${accentColor}15`,
              color: accentColor,
              border: `1px solid ${accentColor}30`,
            }}
          >
            <span>View Alerts ({activeAlerts.length})</span>
          </Button>

          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-gray-400 hover:text-gray-600"
            title="Dismiss notification for now"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Pop-up Details Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="max-w-xl max-h-[85vh] flex flex-col p-0 overflow-hidden rounded-2xl"
          style={{
            backgroundColor: theme.cardBackground,
            borderColor: theme.divider,
            color: theme.text,
          }}
        >
          <div
            className="p-5 border-b flex items-center justify-between"
            style={{
              borderColor: theme.divider,
              backgroundColor: isDark ? '#111827' : '#F8FAFC',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  backgroundColor: `${accentColor}20`,
                  color: accentColor,
                }}
              >
                {criticalCount > 0 ? (
                  <AlertOctagon className="w-5 h-5" />
                ) : (
                  <AlertTriangle className="w-5 h-5" />
                )}
              </div>
              <div>
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <span>Budget Overspending Warnings</span>
                  <span
                    className="text-xs font-black px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: `${accentColor}20`,
                      color: accentColor,
                    }}
                  >
                    {activeAlerts.length} Active
                  </span>
                </DialogTitle>
                <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
                  Day {currentDay} of month • Predictive burn-rate engine detected spending pace anomalies
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setIsDismissed(true);
                setModalOpen(false);
              }}
              className="text-xs font-semibold px-2.5 py-1 rounded-lg border transition-colors hover:bg-black/5 dark:hover:bg-white/5"
              style={{ borderColor: theme.divider, color: theme.textSecondary }}
            >
              Dismiss
            </button>
          </div>

          {/* Alerts List */}
          <div className="p-5 overflow-y-auto space-y-3.5 flex-1">
            {activeAlerts.map((item) => {
              const isOver = item.status === BUDGET_STATUS.OVERBUDGET;
              const cardAccent = isOver ? '#EF4444' : '#F59E0B';

              return (
                <div
                  key={item.category}
                  className="p-4 rounded-xl border transition-all"
                  style={{
                    backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                    borderColor: isDark ? '#374151' : '#E2E8F0',
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ color: theme.text }}>
                        {item.category}
                      </span>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${cardAccent}20`,
                          color: cardAccent,
                        }}
                      >
                        {item.statusTitle}
                      </span>
                    </div>

                    <div className="text-right">
                      <span className="text-sm font-bold" style={{ color: cardAccent }}>
                        {formatCurrency(item.spent)}
                      </span>
                      <span className="text-xs text-gray-400">
                        {' '}/ {formatCurrency(item.budgetLimit)}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div
                    className="w-full h-2 rounded-full overflow-hidden mb-2.5"
                    style={{ backgroundColor: isDark ? '#374151' : '#E2E8F0' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(item.percentage, 100)}%`,
                        backgroundColor: cardAccent,
                      }}
                    />
                  </div>

                  {/* Metrics & Currency-Aware Advice */}
                  <div
                    className="p-2.5 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs"
                    style={{
                      backgroundColor: isDark ? '#111827' : '#F8FAFC',
                      border: `1px solid ${theme.divider}`,
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      <Flame className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                      <span style={{ color: theme.textSecondary }}>
                        Daily Burn: <strong style={{ color: theme.text }}>{formatCurrency(item.dailyBurnRate)}/day</strong>
                      </span>
                      <span className="text-gray-400">•</span>
                      <span style={{ color: theme.textSecondary }}>
                        Est: <strong style={{ color: cardAccent }}>{formatCurrency(item.projectedTotal)}</strong>
                      </span>
                    </div>

                    <div className="font-semibold text-right" style={{ color: cardAccent }}>
                      {getFormattedAdvice(item)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className="p-4 border-t flex items-center justify-between"
            style={{
              borderColor: theme.divider,
              backgroundColor: isDark ? '#111827' : '#F8FAFC',
            }}
          >
            <span className="text-xs" style={{ color: theme.textSecondary }}>
              Tip: You can set or adjust spending limits anytime below.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              style={{ borderColor: theme.divider, color: theme.text }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
